import { useState, useMemo, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { useLingui, Trans } from "@lingui/react/macro";
import { useQueryClient } from "@tanstack/react-query";
import { format, addDays, isWeekend } from "date-fns";
import { BaseEditableTable } from "@/components/table/BaseEditableTable";
import type { EditableCellConfig } from "@/components/table/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { getDateFnsLocale } from "@/lib/format";
import { TimePicker } from "@/components/ui/time-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { CalendarPlus, Copy, Plus, Trash2, UserX } from "lucide-react";
import {
  useCreateScheduleEntries,
  useUpdateScheduleEntry,
  useDeleteScheduleEntry,
  useCreateTrackingEntry,
  useUpdateTrackingEntry,
  useDeleteTrackingEntry,
  useBulkCreateTrackingEntries,
  settlingInKeys,
} from "@/hooks/useSettlingIn";
import type { ScheduleEntryResponse, TrackingEntryResponse } from "@/types/settlingIn";
import { useChildRelationships } from "@/hooks/useChildContactRelationships";
import { useClosureDates } from "@/hooks/usePublicHolidays";
import { EmployeeSearchSelect } from "@/components/settling-in/EmployeeSearchSelect";
import { buildScheduleSeedEntries } from "./buildScheduleSeedEntries";
import { buildDayMicroBar, deriveDayWindow } from "./buildDayMicroBar";
import { DayMicroBar } from "@/components/settling-in/DayMicroBar";
import { getIndicatorStyle } from "@/lib/settlingInIndicator";
import { cn } from "@/lib/utils";
import {
  formatOrgTime,
  orgWallClockToISO,
  toOrgDateOnly,
  parseDateString,
  formatDate,
} from "@/utils/dateUtils";
import { varianceMinutes } from "./settlingInVariance";
import { computeNextScheduleDate } from "./computeNextScheduleDate";
import {
  buildDayRows,
  parseDayKey,
  dayStatusToIndicatorValue,
  type DayRow,
  type DayStatus,
} from "./dayRow";

// `parseDayKey` is the single shared "YYYY-MM-DD" parser (dayRow.ts); it returns
// undefined for unparseable input, which the callers here treat as "today".
function parseCalendarDate(input: string): Date {
  return parseDayKey(input) ?? new Date();
}

// "HH:MM" is well-formed (hours < 24, minutes < 60). Empty is allowed (a cleared cell).
function isValidTime(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return true;
  const match = /^(\d{2}):(\d{2})$/.exec(String(value));
  if (!match) return false;
  return Number(match[1]) < 24 && Number(match[2]) < 60;
}

/**
 * The signed child-present variance for a day, with explicit `no record` /
 * `absent` labels (never blank) and quiet (no ink) on-plan rows. Mirrors the
 * former TrackingTab `TimeVarianceCell` labelling.
 */
function VarianceCell({ row }: { row: DayRow }) {
  const { t, i18n } = useLingui();
  const planned = row.planned_child_present_from;

  // A scheduled day with no tracking entry at all: needs recording.
  if (!row.trackingId) {
    if (!planned) return <span className="text-muted-foreground">—</span>;
    return (
      <span className="text-muted-foreground">
        {t({ id: "settlingIn.days.variance.noRecord", message: "No record" })}
      </span>
    );
  }

  // Tracking exists but NEITHER child-present time is set: an explicit absent day.
  // Requiring both to be empty matters — a half-entered day (arrival typed, departure
  // still blank) is incomplete, and calling it "Absent" would assert the child never came.
  if (!row.actual_child_present_from && !row.actual_child_present_to) {
    return (
      <span className="text-muted-foreground">
        {t({ id: "settlingIn.days.variance.absent", message: "Absent" })}
      </span>
    );
  }

  // Compare BOTH windows. `deriveDayStatus` flags a day as deviated on either the arrival
  // or the departure delta, so comparing arrival alone here renders a quiet "·" next to a
  // "Deviated" chip whenever only the leave time moved.
  const fromDelta = varianceMinutes(planned, row.actual_child_present_from);
  const toDelta = varianceMinutes(row.planned_child_present_to, row.actual_child_present_to);

  const isQuiet =
    (fromDelta === null || fromDelta === 0) && (toDelta === null || toDelta === 0);
  // On-plan (or nothing to compare): quiet, no ink (§2.4).
  if (isQuiet) {
    return <span className="text-muted-foreground" aria-hidden="true">·</span>;
  }

  // Surface the larger deviation, and say which end of the day it belongs to.
  const showDeparture =
    (fromDelta === null || fromDelta === 0) ||
    (toDelta !== null && Math.abs(toDelta) > Math.abs(fromDelta));
  const delta = (showDeparture ? toDelta : fromDelta) as number;

  const text =
    delta > 0
      ? i18n._({ id: "settlingIn.days.variance.plus", message: "+{n} min", values: { n: delta } })
      : i18n._({ id: "settlingIn.days.variance.minus", message: "−{n} min", values: { n: Math.abs(delta) } });

  return (
    <span className="text-capacity-warning-on-surface">
      <span aria-hidden="true">{text}</span>
      <span className="sr-only">
        {showDeparture
          ? i18n._({
              id: "settlingIn.days.variance.aria.departure",
              message: "Departure deviates from plan by {n} minutes",
              values: { n: delta },
            })
          : i18n._({
              id: "settlingIn.days.variance.aria.arrival",
              message: "Arrival deviates from plan by {n} minutes",
              values: { n: delta },
            })}
      </span>
    </span>
  );
}

/**
 * The tri-state day-status chip, rendered via the reused `getIndicatorStyle`
 * vocabulary (never a settling-* token family) with its non-colour channel
 * (shape + icon + glyph) plus a day-status-specific localized label.
 */
function StatusCell({ row }: { row: DayRow }) {
  const { i18n } = useLingui();
  const style = getIndicatorStyle(dayStatusToIndicatorValue(row.dayStatus));
  const Icon = style.icon;

  const labels: Record<DayStatus, string> = {
    ok: i18n._({ id: "settlingIn.days.status.ok", message: "On plan" }),
    deviated: i18n._({ id: "settlingIn.days.status.deviated", message: "Deviated" }),
    "missing-actual": i18n._({ id: "settlingIn.days.status.missingActual", message: "No record" }),
  };
  const ariaLabels: Record<DayStatus, string> = {
    ok: i18n._({ id: "settlingIn.days.status.aria.ok", message: "Day recorded on plan" }),
    deviated: i18n._({ id: "settlingIn.days.status.aria.deviated", message: "Day deviated from plan" }),
    "missing-actual": i18n._({
      id: "settlingIn.days.status.aria.missingActual",
      message: "Scheduled day with no recorded actual",
    }),
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 text-xs",
        style.chip,
        style.text,
        style.shape,
      )}
      aria-label={ariaLabels[row.dayStatus]}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      <span aria-hidden="true">{style.glyph}</span>
      <span>{labels[row.dayStatus]}</span>
    </span>
  );
}

interface DaysTabProps {
  planId: string;
  scheduleEntries: ScheduleEntryResponse[];
  trackingEntries: TrackingEntryResponse[];
  childId: string;
  startDate?: string;
  completed: boolean;
}

export function DaysTab({
  planId,
  scheduleEntries,
  trackingEntries,
  childId,
  startDate,
  completed,
}: DaysTabProps) {
  const { t, i18n } = useLingui();
  const queryClient = useQueryClient();
  const [deleteRowId, setDeleteRowId] = useState<string | null>(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showMarkAbsent, setShowMarkAbsent] = useState(false);

  const createScheduleMutation = useCreateScheduleEntries();
  const updateScheduleMutation = useUpdateScheduleEntry();
  const deleteScheduleMutation = useDeleteScheduleEntry();
  const createTrackingMutation = useCreateTrackingEntry();
  const updateTrackingMutation = useUpdateTrackingEntry();
  // Silent: a row delete removes the actual and the planned entry and toasts once.
  const deleteTrackingMutation = useDeleteTrackingEntry({ silent: true });
  const bulkCreateTrackingMutation = useBulkCreateTrackingEntries();

  const { data: relationships } = useChildRelationships(childId, !!childId);
  // Take the STATUS too. While the closure query is loading or has errored `holidays` is
  // undefined, and treating that as "no closures" would generate settling-in days on
  // public holidays and daycare-closure days — days nobody can attend.
  const { data: holidays, status: closureStatus } = useClosureDates();
  const closuresReady = closureStatus === "success" && holidays !== undefined;

  const contactOptions = useMemo(() => {
    if (!relationships || !Array.isArray(relationships)) return [];
    return relationships
      .filter((r: { contactId?: string }) => r.contactId)
      .map((r: { contactId?: string; contactFirstname?: string; contactLastname?: string }) => ({
        value: r.contactId || "",
        label: `${r.contactFirstname || ""} ${r.contactLastname || ""}`.trim(),
      }));
  }, [relationships]);

  const dayRows = useMemo(
    () =>
      buildDayRows(scheduleEntries, trackingEntries, {
        locale: getDateFnsLocale(i18n.locale),
      }),
    [scheduleEntries, trackingEntries, i18n.locale],
  );

  // --- Toolbar state (Generate days / Mark absent) ---
  const defaultGenStart = useMemo(() => {
    const lastSchedule = [...scheduleEntries]
      .map((e) => toOrgDateOnly(e.date))
      .filter(Boolean)
      .sort()
      .pop();
    return computeNextScheduleDate({ lastEntryDate: lastSchedule, planStartDate: startDate });
  }, [scheduleEntries, startDate]);

  const [genStart, setGenStart] = useState(defaultGenStart);
  const [genDays, setGenDays] = useState(5);
  const [genChildFrom, setGenChildFrom] = useState("08:00");
  const [genChildTo, setGenChildTo] = useState("11:00");
  const [genContactFrom, setGenContactFrom] = useState("08:00");
  const [genContactTo, setGenContactTo] = useState("09:00");

  const [absStart, setAbsStart] = useState(defaultGenStart);
  const [absEnd, setAbsEnd] = useState(defaultGenStart);

  const holidaySet = useMemo(
    () => new Set((holidays ?? []).map((h) => h.holidayDate).filter(Boolean) as string[]),
    [holidays],
  );

  const closuresUnavailableMessage = useMemo(
    () =>
      i18n._({
        id: "settlingIn.days.closuresUnavailable",
        message:
          "Holiday and closure dates could not be loaded. Generating days is unavailable until they load.",
      }),
    // `i18n` comes from the subscribing `useLingui()` hook, so its identity changes on a
    // locale switch and the message re-resolves (same reasoning as the `columns` memo).
    [i18n],
  );

  // A failed closure load is not a silent condition: the toolbar actions that depend on it
  // are disabled, so the user needs to be told why rather than left with dead buttons.
  useEffect(() => {
    if (closureStatus === "error") {
      toast.error(closuresUnavailableMessage);
    }
  }, [closureStatus, closuresUnavailableMessage]);

  // Marking absent only ever applies to a day the plan schedules, so the range defaults to
  // the first scheduled day still missing an actual — the day an admin backfilling a paper
  // list is actually looking at. `defaultGenStart` (the NEXT free date) has no schedule
  // entry by construction and would open the range on a day nothing can be marked.
  const defaultAbsentDate = useMemo(() => {
    const firstUntracked = dayRows.find((r) => r.scheduleId && !r.trackingId);
    return firstUntracked?.date ?? dayRows.find((r) => r.scheduleId)?.date ?? defaultGenStart;
  }, [dayRows, defaultGenStart]);

  // --- Cell-update dispatcher: planned → schedule (schedule version),
  //     actual → tracking (tracking version). NEVER a merged version. ---
  const handleCellUpdate = useCallback(
    async (rowId: string, field: keyof DayRow, newValue: DayRow[keyof DayRow]) => {
      const row = dayRows.find((r) => r.id === rowId);
      if (!row) return;
      const dateKey = row.date;
      const fieldStr = String(field);

      const runWithConflictRefetch = async (mutate: () => Promise<unknown>) => {
        try {
          await mutate();
          // AWAIT the refetch. Each write bumps the row's version server-side, and the
          // next cell edit reads its version out of the props-derived rows. Without
          // waiting for the refreshed data to land, tabbing quickly across two cells of
          // the same entry sends the pre-increment version on the second edit and loses
          // it to a spurious 409 — precisely the fast-backfill flow this feature is for.
          await queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(planId) });
        } catch {
          // A stale-version rejection refetches the detail query, re-deriving this row
          // from server truth. It never merges versions and never touches the other
          // column group's edit. The mutation hook's onError already toasted, so the
          // error is deliberately not rethrown — rethrowing would make the table emit a
          // second, generic "Failed to update" toast on top of it.
          await queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(planId) });
        }
      };

      if (fieldStr.startsWith("planned_")) {
        const schedule = row.scheduleEntry;
        if (!schedule?.id) return; // planned cells edit an existing schedule entry only
        if (schedule.version === undefined) {
          // Submitting a made-up version would either fail as a conflict or, worse,
          // overwrite a concurrent edit. Reload instead of guessing.
          await queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(planId) });
          return;
        }
        // Captured after the guard above: narrowing on `schedule.version` is lost
        // inside the callback below, a const alias keeps it.
        const scheduleVersion = schedule.version;
        const scheduleField = fieldStr.replace("planned_", "");
        await runWithConflictRefetch(() =>
          updateScheduleMutation.mutateAsync({
            planId,
            entryId: schedule.id!,
            data: {
              date: toOrgDateOnly(schedule.date),
              employee_id: schedule.employee_id,
              contact_id: schedule.contact_id,
              child_present_from: schedule.child_present_from,
              child_present_to: schedule.child_present_to,
              contact_present_from: schedule.contact_present_from,
              contact_present_to: schedule.contact_present_to,
              comment: schedule.comment,
              [scheduleField]: newValue === "" ? undefined : newValue,
              version: scheduleVersion,
            },
          }),
        );
        return;
      }

      if (fieldStr.startsWith("actual_")) {
        const trackingField = fieldStr.replace("actual_", "");
        const isTimeField = trackingField.endsWith("_from") || trackingField.endsWith("_to");
        const processed = isTimeField
          ? orgWallClockToISO(dateKey, newValue as string)
          : newValue;
        const tracking = row.trackingEntry;

        if (tracking?.id && tracking.version === undefined) {
          // See the schedule branch: never fabricate a version.
          await queryClient.invalidateQueries({ queryKey: settlingInKeys.detail(planId) });
          return;
        }

        if (tracking?.id) {
          await runWithConflictRefetch(() =>
            updateTrackingMutation.mutateAsync({
              planId,
              entryId: tracking.id!,
              data: {
                date: dateKey,
                employee_id: tracking.employee_id,
                contact_id: tracking.contact_id,
                schedule_entry_id: tracking.schedule_entry_id ?? row.scheduleId,
                child_present_from: tracking.child_present_from,
                child_present_to: tracking.child_present_to,
                contact_present_from: tracking.contact_present_from,
                contact_present_to: tracking.contact_present_to,
                comment: tracking.comment,
                [trackingField]: processed,
                // The guard above returned when `tracking.id` is set and `version` is
                // undefined, so inside this branch `version` is always present.
                version: tracking.version!,
              },
            }),
          );
        } else {
          await runWithConflictRefetch(() =>
            createTrackingMutation.mutateAsync({
              planId,
              data: {
                date: dateKey,
                schedule_entry_id: row.scheduleId,
                [trackingField]: processed,
              },
            }),
          );
        }
      }
    },
    [dayRows, planId, queryClient, updateScheduleMutation, updateTrackingMutation, createTrackingMutation],
  );

  /**
   * The shared day timeline every row's micro-bar is drawn against (ST-6/S20),
   * computed once so the bars are comparable down the column.
   */
  const dayWindow = useMemo(() => deriveDayWindow(dayRows), [dayRows]);

  const columns = useMemo<EditableCellConfig<DayRow>[]>(() => {
    const editable = !completed;
    // Planned cells write to an EXISTING schedule entry (see handleCellUpdate), so a
    // tracking-only row has nothing to update. Gating editability on the same
    // `!row.scheduleId` guard the employee selector already uses keeps the editor from
    // opening at all, instead of accepting a value the update handler silently drops.
    const plannedEditable = (row: DayRow) => editable && !!row.scheduleId;
    const timeValidation = (v: unknown): string | null =>
      isValidTime(v)
        ? null
        : t({ id: "settlingIn.days.invalidTime", message: "Enter a valid time (HH:MM)" });

    const cols: EditableCellConfig<DayRow>[] = [
      {
        accessorKey: "date",
        id: "day",
        header: t({ id: "settlingIn.days.columns.day", message: "Day" }),
        editable: false,
        cell: ({ row }: { row: { original: DayRow } }) => (
          <div className="leading-tight">
            <span className="block text-xs uppercase tracking-wide text-muted-foreground">
              {i18n._({
                id: "settlingIn.days.weekLabel",
                message: "Wk {n}",
                values: { n: row.original.weekIndex },
              })}
            </span>
            <span className="block text-sm">
              {row.original.weekdayLabel} {formatDate(parseDateString(row.original.date), i18n.locale)}
            </span>
            {/* Decorative plan-vs-actual bar (ST-6/S20). aria-hidden: the
                numeric time columns and Variance remain authoritative. */}
            <DayMicroBar bar={buildDayMicroBar(row.original, dayWindow)} className="mt-1" />
          </div>
        ),
      },
      // Planned group (edits the schedule entry, schedule version).
      {
        accessorKey: "planned_child_present_from",
        header: t({ id: "settlingIn.days.columns.plannedChildFrom", message: "Planned · Child from" }),
        inputType: "time",
        editable: plannedEditable,
        validation: timeValidation,
      },
      {
        accessorKey: "planned_child_present_to",
        header: t({ id: "settlingIn.days.columns.plannedChildTo", message: "Planned · Child to" }),
        inputType: "time",
        editable: plannedEditable,
        validation: timeValidation,
      },
      {
        accessorKey: "planned_contact_present_from",
        header: t({ id: "settlingIn.days.columns.plannedContactFrom", message: "Planned · Contact from" }),
        inputType: "time",
        editable: plannedEditable,
        validation: timeValidation,
      },
      {
        accessorKey: "planned_contact_present_to",
        header: t({ id: "settlingIn.days.columns.plannedContactTo", message: "Planned · Contact to" }),
        inputType: "time",
        editable: plannedEditable,
        validation: timeValidation,
      },
      {
        accessorKey: "planned_employee_id",
        header: t({ id: "settlingIn.days.columns.plannedEmployee", message: "Planned · Staff" }),
        editable: false,
        cell: ({ row }: { row: { original: DayRow } }) => (
          <EmployeeSearchSelect
            value={row.original.planned_employee_id}
            disabled={completed || !row.original.scheduleId}
            onChange={(id) =>
              row.original.scheduleId && handleCellUpdate(row.original.id, "planned_employee_id", id ?? "")
            }
            ariaLabel={i18n._({
              id: "settlingIn.days.employeeForDate",
              message: "Responsible employee for {date}",
              values: { date: formatDate(parseDateString(row.original.date), i18n.locale) },
            })}
          />
        ),
      },
      {
        accessorKey: "planned_contact_id",
        header: t({ id: "settlingIn.days.columns.plannedContact", message: "Planned · Contact" }),
        inputType: "enhanced-select",
        editable: plannedEditable,
        selectOptions: contactOptions,
      },
      // Actual group (edits/creates the tracking entry, tracking version).
      {
        accessorKey: "actual_child_present_from",
        header: t({ id: "settlingIn.days.columns.actualChildFrom", message: "Actual · Child from" }),
        inputType: "time",
        editable,
        validation: timeValidation,
        getEditValue: (v: unknown) => formatOrgTime(v as string),
        cell: ({ row }: { row: { original: DayRow } }) => (
          <span>{formatOrgTime(row.original.actual_child_present_from) || "—"}</span>
        ),
      },
      {
        accessorKey: "actual_child_present_to",
        header: t({ id: "settlingIn.days.columns.actualChildTo", message: "Actual · Child to" }),
        inputType: "time",
        editable,
        validation: timeValidation,
        getEditValue: (v: unknown) => formatOrgTime(v as string),
        cell: ({ row }: { row: { original: DayRow } }) => (
          <span>{formatOrgTime(row.original.actual_child_present_to) || "—"}</span>
        ),
      },
      {
        accessorKey: "actual_contact_present_from",
        header: t({ id: "settlingIn.days.columns.actualContactFrom", message: "Actual · Contact from" }),
        inputType: "time",
        editable,
        validation: timeValidation,
        getEditValue: (v: unknown) => formatOrgTime(v as string),
        cell: ({ row }: { row: { original: DayRow } }) => (
          <span>{formatOrgTime(row.original.actual_contact_present_from) || "—"}</span>
        ),
      },
      {
        accessorKey: "actual_contact_present_to",
        header: t({ id: "settlingIn.days.columns.actualContactTo", message: "Actual · Contact to" }),
        inputType: "time",
        editable,
        validation: timeValidation,
        getEditValue: (v: unknown) => formatOrgTime(v as string),
        cell: ({ row }: { row: { original: DayRow } }) => (
          <span>{formatOrgTime(row.original.actual_contact_present_to) || "—"}</span>
        ),
      },
      // Variance (display-only).
      {
        accessorKey: "dayStatus",
        id: "variance",
        header: t({ id: "settlingIn.days.columns.variance", message: "Variance" }),
        editable: false,
        cell: ({ row }: { row: { original: DayRow } }) => <VarianceCell row={row.original} />,
      },
      // Status chip (display-only).
      {
        accessorKey: "dayStatus",
        id: "status",
        header: t({ id: "settlingIn.days.columns.status", message: "Status" }),
        editable: false,
        cell: ({ row }: { row: { original: DayRow } }) => <StatusCell row={row.original} />,
      },
    ];

    if (!completed) {
      cols.push({
        accessorKey: "id",
        id: "actions",
        header: "",
        editable: false,
        cell: ({ row }: { row: { original: DayRow } }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteRowId(row.original.id)}
            aria-label={i18n._({ id: "settlingIn.days.deleteDay", message: "Delete day" })}
          >
            <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
          </Button>
        ),
      });
    }

    return cols;
    // `t`/`i18n` come from the subscribing `useLingui()` hook, so both identities change
    // on a locale switch and the columns rebuild. (The "depend on i18n.locale, not i18n"
    // rule guards against the non-subscribing `import { i18n } from '@lingui/core'`.)
  }, [t, i18n, completed, contactOptions, handleCellUpdate, dayWindow]);

  const handleDelete = useCallback(
    async (rowId: string) => {
      const row = dayRows.find((r) => r.id === rowId);
      if (!row) return;
      // Remove the whole day: its recorded actual (if any) then its planned entry.
      if (row.trackingId) {
        await deleteTrackingMutation.mutateAsync({ planId, entryId: row.trackingId });
      }
      if (row.scheduleId) {
        // Its hook toasts for the whole row.
        await deleteScheduleMutation.mutateAsync({ planId, entryId: row.scheduleId });
      } else if (row.trackingId) {
        toast.success(t({ id: 'settlingIn.tracking.success.deleted', message: 'Tracking entry deleted' }));
      }
    },
    [dayRows, planId, deleteTrackingMutation, deleteScheduleMutation, t],
  );

  // --- S14 fast-backfill toolbar actions ---

  // Copy planned → actual: seed one tracking entry per scheduled day with no
  // actual yet, equal to the planned window, in ONE bulk backend call.
  const handleCopyPlannedToActual = useCallback(async () => {
    const entries = dayRows
      .filter((r) => r.scheduleId && !r.trackingId)
      .map((r) => ({
        date: r.date,
        schedule_entry_id: r.scheduleId,
        employee_id: r.planned_employee_id || undefined,
        contact_id: r.planned_contact_id || undefined,
        child_present_from: orgWallClockToISO(r.date, r.planned_child_present_from),
        child_present_to: orgWallClockToISO(r.date, r.planned_child_present_to),
        contact_present_from: orgWallClockToISO(r.date, r.planned_contact_present_from),
        contact_present_to: orgWallClockToISO(r.date, r.planned_contact_present_to),
      }));
    if (entries.length === 0) return;
    await bulkCreateTrackingMutation.mutateAsync({ planId, data: { entries } });
  }, [dayRows, planId, bulkCreateTrackingMutation]);

  // Carry-forward + Day: pre-fill times/employee/contact from the previous row.
  const handleAddDay = useCallback(async () => {
    const lastSchedule = [...scheduleEntries]
      .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
      .pop();
    const nextDate = computeNextScheduleDate({
      lastEntryDate: lastSchedule?.date,
      planStartDate: startDate,
    });
    await createScheduleMutation.mutateAsync({
      planId,
      data: {
        entries: [
          {
            date: nextDate,
            employee_id: lastSchedule?.employee_id || undefined,
            contact_id: lastSchedule?.contact_id || undefined,
            child_present_from: lastSchedule?.child_present_from || "08:00",
            child_present_to: lastSchedule?.child_present_to || "11:00",
            contact_present_from: lastSchedule?.contact_present_from || "08:00",
            contact_present_to: lastSchedule?.contact_present_to || "09:00",
          },
        ],
      },
    });
  }, [scheduleEntries, startDate, planId, createScheduleMutation]);

  // Generate days: weekday-patterned schedule over a range, skipping weekends and
  // closure/holiday days, via the existing schedule bulk route (one call).
  const handleGenerateDays = useCallback(async () => {
    if (!closuresReady) {
      toast.error(closuresUnavailableMessage);
      return;
    }
    if (genDays < 1 || genDays > 30) return;
    const lastSchedule = [...scheduleEntries]
      .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
      .pop();
    const employeeId = lastSchedule?.employee_id || undefined;
    const contactId = lastSchedule?.contact_id || undefined;
    const scheduledDates = new Set(
      scheduleEntries.map((e) => toOrgDateOnly(e.date)).filter(Boolean) as string[],
    );

    // Shared with the create dialog's optional seed step (ST-5/S21), so the
    // weekend / closure / duplicate-date skipping lives in exactly one place.
    const entries = buildScheduleSeedEntries({
      start: parseCalendarDate(genStart),
      days: genDays,
      holidays: holidaySet,
      alreadyScheduled: scheduledDates,
      employeeId,
      contactId,
      childFrom: genChildFrom,
      childTo: genChildTo,
      contactFrom: genContactFrom,
      contactTo: genContactTo,
    });
    if (entries.length === 0) return;
    await createScheduleMutation.mutateAsync({ planId, data: { entries } });
    setShowGenerate(false);
  }, [
    genDays,
    genStart,
    genChildFrom,
    genChildTo,
    genContactFrom,
    genContactTo,
    holidaySet,
    closuresReady,
    closuresUnavailableMessage,
    scheduleEntries,
    planId,
    createScheduleMutation,
  ]);

  // Mark absent for a range: record an explicit absent actual (a tracking entry
  // with a null child-present window) for each un-tracked day in the range, in one
  // bulk call. deriveDayStatus reads these as neither missing-actual nor deviated.
  const handleMarkAbsent = useCallback(async () => {
    if (!closuresReady) {
      toast.error(closuresUnavailableMessage);
      return;
    }
    const start = parseCalendarDate(absStart);
    const end = parseCalendarDate(absEnd);
    if (end < start) return;
    const trackedDates = new Set(dayRows.filter((r) => r.trackingId).map((r) => r.date));
    const scheduleByDate = new Map(dayRows.map((r) => [r.date, r.scheduleId]));

    const entries: Array<{ date: string; schedule_entry_id?: string }> = [];
    let current = start;
    let guard = 0;
    while (current <= end && guard < 400) {
      const key = format(current, "yyyy-MM-dd");
      // Only mark days the plan actually schedules. Walking the raw calendar invents
      // tracking rows for weekends, holidays and never-scheduled dates, and those then
      // appear in the grid as brand-new days the admin never planned. "Absent" is a
      // statement about a planned day, so an unplanned day cannot be absent.
      const isSchedulable =
        scheduleByDate.get(key) !== undefined &&
        !isWeekend(current) &&
        !holidaySet.has(key);
      if (isSchedulable && !trackedDates.has(key)) {
        entries.push({ date: key, schedule_entry_id: scheduleByDate.get(key) });
      }
      current = addDays(current, 1);
      guard += 1;
    }
    if (entries.length === 0) return;
    await bulkCreateTrackingMutation.mutateAsync({ planId, data: { entries } });
    setShowMarkAbsent(false);
  }, [
    absStart,
    absEnd,
    dayRows,
    holidaySet,
    closuresReady,
    closuresUnavailableMessage,
    planId,
    bulkCreateTrackingMutation,
  ]);

  const rowClassName = useCallback(
    (row: DayRow) =>
      row.dayStatus === "missing-actual" ? "border-l-2 border-l-capacity-warning" : undefined,
    [],
  );

  return (
    <div className="space-y-4">
      {!completed && (
        <div className="flex flex-wrap justify-end gap-2">
          {/* Generate days */}
          <Popover open={showGenerate} onOpenChange={setShowGenerate}>
            <PopoverTrigger asChild>
              <Button variant="outline" onClick={() => setGenStart(defaultGenStart)}>
                <CalendarPlus className="h-4 w-4 mr-1" aria-hidden="true" />
                <Trans id="settlingIn.days.generate">Generate days</Trans>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 space-y-3">
              <div>
                <Label>{t({ id: "settlingIn.days.generate.startDate", message: "Start date" })}</Label>
                <DatePicker
                  value={genStart}
                  onChange={(date) =>
                    setGenStart(date ? format(date, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"))
                  }
                />
              </div>
              <div>
                <Label>{t({ id: "settlingIn.days.generate.numberOfDays", message: "Number of weekdays" })}</Label>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={genDays}
                  onChange={(e) => setGenDays(Math.min(30, Math.max(1, Number(e.target.value))))}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t({ id: "settlingIn.days.generate.childFrom", message: "Child from" })}</Label>
                  <TimePicker value={genChildFrom} onChange={setGenChildFrom} />
                </div>
                <div>
                  <Label>{t({ id: "settlingIn.days.generate.childTo", message: "Child to" })}</Label>
                  <TimePicker value={genChildTo} onChange={setGenChildTo} />
                </div>
                <div>
                  <Label>{t({ id: "settlingIn.days.generate.contactFrom", message: "Contact from" })}</Label>
                  <TimePicker value={genContactFrom} onChange={setGenContactFrom} />
                </div>
                <div>
                  <Label>{t({ id: "settlingIn.days.generate.contactTo", message: "Contact to" })}</Label>
                  <TimePicker value={genContactTo} onChange={setGenContactTo} />
                </div>
              </div>
              <Button
                className="w-full"
                onClick={handleGenerateDays}
                // Closure dates gate this action: without them the generator would
                // happily plan days on holidays and closure days.
                disabled={createScheduleMutation.isPending || !closuresReady}
              >
                <Trans id="settlingIn.days.generate.submit">Generate</Trans>
              </Button>
            </PopoverContent>
          </Popover>

          {/* Copy planned → actual */}
          <Button
            variant="outline"
            onClick={handleCopyPlannedToActual}
            disabled={bulkCreateTrackingMutation.isPending}
          >
            <Copy className="h-4 w-4 mr-1" aria-hidden="true" />
            <Trans id="settlingIn.days.copyPlanned">Copy planned → actual</Trans>
          </Button>

          {/* Mark absent for a range */}
          <Popover open={showMarkAbsent} onOpenChange={setShowMarkAbsent}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                onClick={() => {
                  // Re-seed the range each time the popover opens; otherwise it keeps
                  // whatever "next schedule date" was current at first mount, which is
                  // stale as soon as days have been generated.
                  setAbsStart(defaultAbsentDate);
                  setAbsEnd(defaultAbsentDate);
                }}
              >
                <UserX className="h-4 w-4 mr-1" aria-hidden="true" />
                <Trans id="settlingIn.days.markAbsent">Mark absent</Trans>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 space-y-3">
              <div>
                <Label>{t({ id: "settlingIn.days.markAbsent.startDate", message: "From" })}</Label>
                <DatePicker
                  value={absStart}
                  onChange={(date) =>
                    setAbsStart(date ? format(date, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"))
                  }
                />
              </div>
              <div>
                <Label>{t({ id: "settlingIn.days.markAbsent.endDate", message: "To" })}</Label>
                <DatePicker
                  value={absEnd}
                  onChange={(date) =>
                    setAbsEnd(date ? format(date, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"))
                  }
                />
              </div>
              <Button
                className="w-full"
                onClick={handleMarkAbsent}
                // Same gate as Generate — an absent day is still filtered against closures.
                disabled={bulkCreateTrackingMutation.isPending || !closuresReady}
              >
                <Trans id="settlingIn.days.markAbsent.submit">Mark absent</Trans>
              </Button>
            </PopoverContent>
          </Popover>

          {/* Carry-forward single day */}
          <Button onClick={handleAddDay} disabled={createScheduleMutation.isPending}>
            <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
            <Trans id="settlingIn.days.addDay">+ Day</Trans>
          </Button>
        </div>
      )}

      {dayRows.length === 0 ? (
        <div className="flex items-center justify-center h-32">
          <p className="text-muted-foreground">
            <Trans id="settlingIn.days.emptyState">
              No days yet. Generate days to plan the settling-in schedule.
            </Trans>
          </p>
        </div>
      ) : (
        <BaseEditableTable<DayRow>
          data={dayRows}
          columns={columns}
          onCellUpdate={handleCellUpdate}
          tableId="settling-in-days"
          // #1156: was "day", which is not a `DayRow` field, so the default sort
          // silently never applied. The date column's accessor is `date`.
          defaultSortField="date"
          defaultSortOrder="asc"
          rowClassName={rowClassName}
          enableGridKeyboardNav={!completed}
        />
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteRowId}
        onOpenChange={(open) => !open && setDeleteRowId(null)}
        title={<Trans id="settlingIn.days.deleteConfirmTitle">Delete this day?</Trans>}
        description={
          <Trans id="settlingIn.days.deleteConfirmDescription">
            This removes the planned and recorded entries for this day. This action cannot be undone.
          </Trans>
        }
        confirmLabel={<Trans id="common.delete">Delete</Trans>}
        variant="destructive"
        onConfirm={() => (deleteRowId ? handleDelete(deleteRowId) : undefined)}
      />
    </div>
  );
}
